import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChefHat, BookOpen, ListChecks, Sparkles, Clock, Users, Leaf } from 'lucide-react';
import { NutriHealRecipe, RecipeCategory } from '../services/nutriHealLibrary';

const CATEGORY_DISPLAY_LABELS: Record<RecipeCategory, string> = {
  RESPIRATORY:      'Respiratory',
  DIGESTIVE:        'Digestive',
  CARDIOVASCULAR:   'Cardiovascular',
  RENAL:            'Renal',
  DERMATOLOGICAL:   'Skin & Hair',
  NEUROLOGICAL:     'Brain & Nerves',
  MUSCULOSKELETAL:  'Joints & Muscles',
  ENDOCRINE:        'Hormones & Metabolism',
  IMMUNE:           'Immunity',
  HERBAL_TEAS:      'Herbal Tea',
};

interface NutriHealRecipeCardProps {
  recipe: NutriHealRecipe;
}

const NutriHealRecipeCard: React.FC<NutriHealRecipeCardProps> = ({ recipe }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  return (
    <motion.div
      layout
      className="bg-white border border-orange-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-shadow relative"
    >
      {/* Recipe Image & Basic Info */}
      <div 
        className="cursor-pointer group"
        onClick={toggleExpand}
      >
        <div className="h-48 bg-orange-50 relative overflow-hidden">
          <img 
            src={recipe.imageUrl} 
            alt={recipe.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80&w=800';
            }}
          />
          {recipe.herbalTea && (
            <div className="absolute bottom-4 left-4">
              <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 shadow-sm">
                <Leaf size={10} /> Herbal Tea
              </span>
            </div>
          )}
        </div>

        <div className="p-5">
          <div className="mb-3">
            <span 
              className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest shadow-sm"
              style={{ 
                backgroundColor: recipe.category === 'HERBAL_TEAS' ? '#2D6A4F' : '#E76F51',
                color: 'white'
              }}
            >
              {CATEGORY_DISPLAY_LABELS[recipe.category]}
            </span>
          </div>
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-serif text-xl font-bold text-gray-900 group-hover:text-[#E76F51] transition-colors leading-tight">
              {recipe.name}
            </h3>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              className="text-gray-400 mt-1"
            >
              <ChevronDown size={20} />
            </motion.div>
          </div>

          <p className="text-gray-500 text-sm italic line-clamp-2 mb-4 leading-relaxed">
            "{recipe.therapeuticBenefit}"
          </p>

          <div className="flex flex-wrap gap-2 mb-4">
            {recipe.ailmentTags.slice(0, 3).map((tag, i) => (
              <span key={i} className="text-[10px] font-bold text-orange-400 uppercase tracking-tighter">
                #{tag}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest border-t border-gray-50 pt-4">
            <span className="flex items-center gap-1"><Clock size={12} /> {recipe.prepTime}</span>
            <span className="flex items-center gap-1"><Users size={12} /> {recipe.servings} Servings</span>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-orange-50"
          >
            <div className="p-6 md:p-8 space-y-8 bg-white">
              {/* Highlight Boxes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-orange-50/50 p-5 rounded-2xl border border-orange-100/50">
                  <h4 className="text-[10px] font-black text-[#E76F51] uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                    <Sparkles size={14} /> Ayurvedic Insight
                  </h4>
                  <p className="text-sm text-orange-900 leading-relaxed font-medium">
                    {recipe.ayurvedicNote}
                  </p>
                </div>
                <div className="bg-emerald-50/50 p-5 rounded-2xl border border-emerald-100/50">
                  <h4 className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                    <BookOpen size={14} /> Suitability
                  </h4>
                  <div className="space-y-1">
                    <p className="text-sm text-emerald-900 font-medium capitalize">
                      <span className="text-emerald-600 opacity-60">Dosha:</span> {recipe.doshaSuitability.join(', ')}
                    </p>
                    <p className="text-sm text-emerald-900 font-medium capitalize">
                      <span className="text-emerald-600 opacity-60">Condition:</span> {recipe.condition}
                    </p>
                  </div>
                </div>
              </div>

              {/* Ingredients */}
              <div>
                <h4 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <ChefHat size={16} className="text-[#E76F51]" /> Ingredients
                </h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-8">
                  {recipe.ingredients.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-gray-600 border-b border-gray-50 pb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-200 mt-1.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Steps */}
              <div>
                <h4 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <ListChecks size={16} className="text-[#E76F51]" /> Preparation
                </h4>
                <div className="space-y-4">
                  {recipe.steps.map((step, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-[#E76F51] flex items-center justify-center text-[10px] font-bold">
                        {i + 1}
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed pt-0.5">
                        {step}
                      </p>
                    </div>
                  ))}
                </div>
              </div>


            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default NutriHealRecipeCard;
